//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 19/03/2025
// Version       : 1
// Description   : Para elegir el surtidor y cargar su reporte (Archivo Excel)
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import React, { useState, useEffect, useRef } from "react";
import { UrlApi } from "../../services/apirest";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  DialogContent,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { read, utils } from "xlsx";

// Notification
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

// ICONOS
import FileUploadIcon from "@mui/icons-material/FileUpload";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function CargarDatos({
  handleCloseCargarDatos,
  handleNextStep,
  proveedorSeleccionado,
  setProveedorSeleccionado,
}) {
  const [proveedoresCombustible, setProveedoresCombustible] = useState([]);
  const [previewData, setPreviewData] = useState([]);

  // -------------

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // -------------

  const displayColumns = previewData[0]
    ? Object.keys(previewData[0])
        .filter((k) => k !== "id")
        .map((header) => ({
          field: header,
          headerName: header,
          width: 150,
          disableColumnMenu: true,
        }))
    : []; // Array vacío cuando no hay datos

  // -------------

  const [archivoCargado, setArchivoCargado] = useState(null);
  const fileInputRef = useRef(null);

  const ObtenerProveedoresCombustible = () => {
    fetch(UrlApi + "/proveedores_combustible/activas")
      .then((data) => data.json())
      .then((data) => setProveedoresCombustible(data));
  };

  useEffect(() => {
    ObtenerProveedoresCombustible();
  }, []);

  // *******************************************************************

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setArchivoCargado(file.name);

    // Leer el archivo
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: "array" });

      // Obtener primera hoja
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Convertir a JSON
      const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

      // Crear preview (primeras 5 filas)
      const headers = jsonData[0];

      const preview = jsonData.slice(1).map((row, index) => {
        const rowData = { id: index + 1 }; // Generar ID único basado en índice
        headers.forEach((header, i) => {
          rowData[header] = row[i];
        });
        return rowData;
      });

      setPreviewData(preview);
    };
    reader.readAsArrayBuffer(file);
  };

  // -------------------------------------------------------------------------

  const handleNext = async () => {
    try {
      // Obtener configuración del proveedor
      const configResponse = await fetch(
        `${UrlApi}/proveedores_configuracion_reporte/proveedor/${proveedorSeleccionado}`
      );
      const configData = await configResponse.json();
      if (!configData || configData.length === 0) {
        setErrorMessage(
          "No se encontró una configuración para el proveedor seleccionado."
        );
        setErrorOpen(true);
        return;
      }

      const config = configData[0];

      const formatoFecha = config.formato_fecha || "DMY";

      if (!config) {
        throw new Error("No se encontró configuración para este proveedor");
      }

      if (!previewData || previewData.length === 0) {
        throw new Error("No hay datos para normalizar");
      }

      const fechaColumna = config.fecha; // nombre de la columna "fecha"

      if (!fechaColumna) {
        throw new Error("Configuración inválida: falta columna de fecha");
      }

      // Función para normalizar números
      const normalizarNumero = (valor) => {
        if (valor === null || valor === undefined) return 0;
        const valorString = valor.toString().replace(",", ".");
        const numero = Number(valorString);
        return isNaN(numero) ? 0 : numero;
      };

      // Función para formatear fecha
      const formatearFecha = (fechaStr, horaStr = null) => {
        if (!fechaStr) {
          console.warn("⚠️ Fecha vacía o indefinida:", fechaStr);
          return null;
        }

        try {
          let fechaParte = fechaStr.split(" ")[0]; // Separar fecha y hora si vienen juntas
          let horaParte = fechaStr.split(" ")[1] || horaStr;

          let dia, mes, anio;

          if (formatoFecha === "YMD") {
            // Ej: 2024-04-20
            const partes = fechaParte.includes("-")
              ? fechaParte.split("-")
              : fechaParte.split("/");
            anio = partes[0];
            mes = partes[1];
            dia = partes[2];
          } else if (formatoFecha === "DMY") {
            // Ej: 20/04/2024
            const partes = fechaParte.includes("/")
              ? fechaParte.split("/")
              : fechaParte.split("-");
            dia = partes[0];
            mes = partes[1];
            anio = partes[2];
          } else if (formatoFecha === "MDY") {
            // Ej: 04/20/2024
            const partes = fechaParte.includes("/")
              ? fechaParte.split("/")
              : fechaParte.split("-");
            mes = partes[0];
            dia = partes[1];
            anio = partes[2];
          } else {
            console.error("❌ Formato de fecha desconocido:", formatoFecha);
            return null;
          }

          // Hora
          let horas = 0,
            minutos = 0,
            segundos = 0;
          if (horaParte) {
            const horaPartes = horaParte
              .split(":")
              .map((num) => parseInt(num, 10) || 0);
            [horas, minutos, segundos] = horaPartes;
          }

          // Crear objeto Date
          const fecha = new Date(
            `${anio}-${("0" + mes).slice(-2)}-${("0" + dia).slice(-2)}T${(
              "0" + horas
            ).slice(-2)}:${("0" + minutos).slice(-2)}:${("0" + segundos).slice(
              -2
            )}`
          );

          // Retornar fecha en formato ISO simple
          return `${fecha.getFullYear()}-${("0" + (fecha.getMonth() + 1)).slice(
            -2
          )}-${("0" + fecha.getDate()).slice(-2)} ${(
            "0" + fecha.getHours()
          ).slice(-2)}:${("0" + fecha.getMinutes()).slice(-2)}:${(
            "0" + fecha.getSeconds()
          ).slice(-2)}`;
        } catch (error) {
          console.error("❌ Error formateando fecha:", fechaStr, error);
          return null;
        }
      };

      // Normalizar datos: se crea un objeto nuevo con las propiedades necesarias
      const normalized = previewData.map((row, index) => {
        // Para la fecha, obtenemos el string desde la columna configurada y lo formateamos
        const fechaFormateada = formatearFecha(
          row[config.fecha],
          config.hora ? row[config.hora] : null
        );

        return {
          id: index + 1, // Asigna un id secuencial
          fecha: fechaFormateada, // Fecha normalizada
          patente: row[config.patente] || "", // Extrae la patente desde la columna "IDENTIFICACION TARJETA"
          producto: row[config.producto] || "", // Extrae el producto desde la columna "PRODUCTO"
          cantidad: normalizarNumero(row[config.cantidad]),
          nro_comprobante: row[config.nro_comprobante] || "", // Extrae el número de comprobante desde "REMITO"
        };
      });

      handleNextStep(normalized, config, proveedorSeleccionado);
    } catch (error) {
      console.error("❌ Error en handleNext:", error);
    }
  };

  return (
    <>
      {" "}
      <DialogContent sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Selector de proveedor */}
          <Grid item xs={2}>
            <FormControl fullWidth>
              <InputLabel>Proveedor</InputLabel>
              <Select
                labelId="Proveedor"
                value={proveedorSeleccionado || ""}
                onChange={(e) => setProveedorSeleccionado(e.target.value)}
                label="Proveedor"
                required
                size="small"
              >
                {proveedoresCombustible.map((proveedor) => (
                  <MenuItem key={proveedor.id} value={proveedor.id}>
                    {proveedor.proveedor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} mt={0.7}>
            {archivoCargado && (
              <Typography variant="subtitle1" gutterBottom>
                <b>Archivo:</b> {archivoCargado}
              </Typography>
            )}
          </Grid>
          {/* Botón de carga */}
          <Grid item xs={4}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".xlsx, .ods"
              onChange={handleFileUpload}
            />
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              disabled={!proveedorSeleccionado}
              onClick={() => fileInputRef.current.click()}
              startIcon={<FileUploadIcon />}
            >
              {archivoCargado ? "Cambiar Archivo" : "Cargar Archivo"}
            </Button>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            mb: 8,
            height: "45vh",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e0e0e0", 
            borderRadius: 1, 
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <DataGrid
              rows={previewData}
              columns={displayColumns}
              density="compact"
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableSelectionOnClick
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
              }
              rowSelection={false}
              localeText={{
                noRowsLabel:
                  "Seleccione un archivo para previsualizar los datos",
                noResultsOverlayLabel: "No se encontraron resultados",
              }}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflowX: "auto",
                  minHeight: "30vh",
                  maxHeight: "40vh",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "0.75rem",
                  padding: "4px 8px", // Deshabilita el efecto de focus
                  "&:focus": {
                    outline: "none", // Elimina el borde de enfoque
                  },
                  // Deshabilita el cursor de selección
                  cursor: "default",
                },
                // Elimina el efecto hover
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "inherit !important",
                },
                // Estilos para filas
                "& .MuiDataGrid-row.even": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row.odd": {
                  backgroundColor: "white",
                },
                // Mantener color de las filas al pasar por encima
                "& .MuiDataGrid-row.even:hover": {
                  backgroundColor: "#f5f5f5 !important",
                },
                "& .MuiDataGrid-row.odd:hover": {
                  backgroundColor: "white !important",
                },
              }}
              slotProps={{
                row: {
                  tabIndex: -1, // Deshabilita navegación por teclado
                },
              }}
            />
          </Box>
        </Box>

        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={6} sx={{ textAlign: "left" }}>
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={handleCloseCargarDatos}
              sx={{ minWidth: 100 }}
            >
              Volver
            </Button>
          </Grid>

          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={{ minWidth: 100 }}
              endIcon={<NavigateNextIcon />}
              disabled={!archivoCargado}
              onClick={handleNext}
            >
              Siguiente
            </Button>
          </Grid>
        </Grid>

        <Snackbar
          open={errorOpen}
          autoHideDuration={4000}
          onClose={() => setErrorOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setErrorOpen(false)}
            severity="error"
            sx={{ width: "100%" }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </DialogContent>
    </>
  );
}

export default CargarDatos;
